import { ICustomersHook } from '@models';
import { CustomerService } from '@services';
import { useStore } from '@stores';
import { getDateRangeFromPreset } from '@utils';
import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';

export const useCustomers = (): ICustomersHook => {
  const { customerStore } = useStore();
  const {
    customers,
    totalCount,
    loading,
    currentPage,
    skipCount,
    maxResultCount,
    setSkipCount,
    setCurrentPage,
  } = customerStore;
  const [fullname, setFullname] = useState<string | null>('');
  const [fullnameInput, setFullnameInput] = useState('');
  const [email, setEmail] = useState<string | null>('');
  const [emailInput, setEmailInput] = useState('');
  const [isAppUser, setIsAppUser] = useState<boolean | undefined>(undefined);
  const [datePreset, setDatePreset] = useState<string | undefined>(undefined);
  const [customFrom, setCustomFrom] = useState<string | undefined>(undefined);
  const [customTo, setCustomTo] = useState<string | undefined>(undefined);
  const [sorting, setSorting] = useState<string | undefined>();

  const { createdFrom, createdTo } = useMemo(() => {
    const range =
      datePreset === 'custom'
        ? { startAt: customFrom, endAt: customTo }
        : getDateRangeFromPreset(datePreset);
    return { createdFrom: range.startAt, createdTo: range.endAt };
  }, [datePreset, customFrom, customTo]);

  useEffect(() => {
    return () => customerStore.resetCustomers();
  }, [customerStore]);

  useEffect(() => {
    customerStore.getAll({
      fullname,
      email,
      maxResultCount,
      skipCount,
      isAppUser,
      createdFrom,
      createdTo,
      sorting,
    });
  }, [customerStore, isAppUser, maxResultCount, skipCount, fullname, email, createdFrom, createdTo, sorting]);

  const handlePageChange = (pagination: any) => {
    customerStore.setMaxResultCount(pagination.pageSize);
    customerStore.setSkipCount((pagination.current - 1) * pagination.pageSize);
    customerStore.setCurrentPage(pagination.current);
  };

  const handleSortChange = (value?: string) => {
    setSorting(value);
    setSkipCount(0);
    setCurrentPage(1);
  };

  const handleFullnameSearch = debounce((text: string) => {
    setFullname(text);
    setSkipCount(0);
    setCurrentPage(1);
  }, 500);

  const handleFullnameInputChange = (text: string) => {
    setFullnameInput(text);
    handleFullnameSearch(text);
  };

  const handleEmailSearch = debounce((text: string) => {
    setEmail(text);
    setSkipCount(0);
    setCurrentPage(1);
  }, 500);

  const handleEmailInputChange = (text: string) => {
    setEmailInput(text);
    handleEmailSearch(text);
  };

  const handleIsAppUserChange = (value: boolean | undefined) => {
    setIsAppUser(value);
    setSkipCount(0);
    setCurrentPage(1);
  };

  const handleDatePresetChange = (preset: string) => {
    setDatePreset(preset || undefined);
    if (preset !== 'custom') {
      setCustomFrom(undefined);
      setCustomTo(undefined);
    }
    setSkipCount(0);
    setCurrentPage(1);
  };

  const handleCustomDateRange = (from: string | undefined, to: string | undefined) => {
    setCustomFrom(from);
    setCustomTo(to);
  };

  const refresh = () => {
    customerStore.getAll({
      fullname,
      email,
      maxResultCount,
      skipCount,
      isAppUser,
      createdFrom,
      createdTo,
      sorting,
    });
  };

  const fetchAllForExport = useCallback(async () => {
    const result = await CustomerService.getAll({
      fullname,
      email,
      isAppUser,
      createdFrom,
      createdTo,
      maxResultCount: totalCount || 10000,
      skipCount: 0,
      sorting,
    });
    return result.items;
  }, [fullname, email, isAppUser, createdFrom, createdTo, totalCount, sorting]);

  return {
    loading,
    dataSource: Array.from(customers.values()),
    totalCount,
    currentPage,
    maxResultCount,
    fullnameInput,
    emailInput,
    isAppUser,
    datePreset,
    customFrom,
    customTo,
    handleEmailSearch: handleEmailInputChange,
    handleFullnameSearch: handleFullnameInputChange,
    handlePageChange,
    handleSortChange,
    sorting,
    handleIsAppUserChange,
    handleDatePresetChange,
    handleCustomDateRange,
    refresh,
    fetchAllForExport,
  };
};
